import React, { useState, useRef, useEffect } from 'react';
import { isEmpty } from 'lodash';
import { getDate, getAmount } from '/src/components/SingleSICE/Reusable/FundingRounds/Helpers/fundingViewHelpers';
import { MOB_BADGE_COLORS, getInitials } from '/src/components/ICESections/Shared/mobileListUtils';
import EstimationIndicator from '/src/components/Reusable/EstimationIndicator';
import EntityLink from './Components/EntityLink';

const PortfolioExitsTable = ({ rounds, hasDirAccess }) => {
    const [scrollPos, setScrollPos] = useState(0);
    const [maxScroll, setMaxScroll] = useState(null);
    const [expandedMobileRows, setExpandedMobileRows] = useState([]);
    const wrapperRef = useRef(null);

    const toggleMobileRow = (key) => setExpandedMobileRows(prev =>
        prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    );

    useEffect(() => {
        const el = wrapperRef.current;
        if (!el) return;
        const update = () => {
            setScrollPos(el.scrollLeft);
            setMaxScroll(el.scrollWidth - el.clientWidth);
        };
        update();
        const ro = new ResizeObserver(update);
        ro.observe(el);
        return () => ro.disconnect();
    }, [rounds]);

    const handleScroll = () => {
        const el = wrapperRef.current;
        if (!el) return;
        setScrollPos(el.scrollLeft);
        setMaxScroll(el.scrollWidth - el.clientWidth);
    };

    const scrollTable = (direction) => {
        const el = wrapperRef.current;
        if (!el) return;
        const scrollVal = Math.max(el.clientWidth - 149, 150);
        el.scrollLeft += direction === 'left' ? -scrollVal : scrollVal;
    };

    if (!rounds || rounds.length === 0) {
        return null;
    }

    return (
        <div className="structured-investments-table-shell">
            <EstimationIndicator />

            {/* desktop / tablet: horizontally scrollable table */}
            <div className="structured-investments-table-scroll-area">
                {scrollPos > 0 && (
                    <button
                        className="structured-investments-scroll-btn structured-investments-scroll-btn--left"
                        onClick={() => scrollTable('left')}
                    >
                        <i className="fas fa-chevron-left" />
                    </button>
                )}
                {maxScroll > 0 && scrollPos < maxScroll && (
                    <button
                        className="structured-investments-scroll-btn structured-investments-scroll-btn--right"
                        onClick={() => scrollTable('right')}
                    >
                        <i className="fas fa-chevron-right" />
                    </button>
                )}
                <div
                    className="structured-investments-table-wrapper"
                    ref={wrapperRef}
                    onScroll={handleScroll}
                >
                    <table className="structured-investments-table structured-exits-table">
                        <thead>
                            <tr className="structured-investments-table__header">
                                <th>Name</th>
                                <th>HQ</th>
                                <th>Primary Industry</th>
                                <th>Exit Type</th>
                                <th>Exit Date</th>
                                <th>Years to Exit</th>
                                <th>Exit Value</th>
                                <th>Total Funding</th>
                                <th>Total Deal Count</th>
                                <th>Acquirer</th>
                                <th>Acquirer Type</th>
                                <th>Acquirer Country</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rounds.map((round, index) => {
                                if (!round) return null;

                                const acquiredEntity = round.investedin_data;
                                const hq = round.location_data?.name || 'N/A';

                                let primaryIndustry = 'N/A';
                                if (round.primary_industry_data?.length > 0 && round.primary_industry_data[0].name) {
                                    primaryIndustry = round.primary_industry_data[0].name;
                                }

                                let exitType = 'N/A';
                                if (!isEmpty(round.funding_stage_data)) {
                                    exitType = round.funding_stage_data.name;
                                }

                                const acquirerType = round.acquirer_type || 'N/A';
                                const acquirerCountry = round.acquirer_hq_data?.[0]?.name || 'N/A';

                                return (
                                    <tr key={`exit-tr-${index}`}>
                                        <td data-label="Name">
                                            <EntityLink entity={acquiredEntity} hasDirAccess={hasDirAccess} />
                                        </td>
                                        <td data-label="HQ">{hq}</td>
                                        <td data-label="Primary Industry">{primaryIndustry}</td>
                                        <td data-label="Exit Type">{exitType}</td>
                                        <td data-label="Exit Date">{getDate(round.date_raised)}</td>
                                        <td data-label="Years to Exit">{round.time_to_exit || 'N/A'}</td>
                                        <td data-label="Exit Value">{getAmount(round.amount)}</td>
                                        <td data-label="Total Funding">{getAmount(round.total_amount_raised)}</td>
                                        <td data-label="Total Deal Count">{round.investedin_funding_count}</td>
                                        <td data-label="Acquirer">
                                            {round.investors_data?.length > 0
                                                ? round.investors_data.map((acq, i) => (
                                                    <EntityLink key={acq?.id ?? i} entity={acq} hasDirAccess={hasDirAccess} />
                                                ))
                                                : <span>N/A</span>
                                            }
                                        </td>
                                        <td data-label="Acquirer Type">{acquirerType}</td>
                                        <td data-label="Acquirer Country">{acquirerCountry}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* mobile: tappable card list */}
            <div className="structured-investments-mobile-list">
                {rounds.map((round, idx) => {
                    if (!round) return null;

                    const key = `exit-mob-${idx}`;
                    const isOpen = expandedMobileRows.includes(key);
                    const col = MOB_BADGE_COLORS[idx % MOB_BADGE_COLORS.length];

                    const acquiredEntity = round.investedin_data;
                    const hq = round.location_data?.name || 'N/A';

                    let primaryIndustry = 'N/A';
                    if (round.primary_industry_data?.length > 0 && round.primary_industry_data[0].name) {
                        primaryIndustry = round.primary_industry_data[0].name;
                    }

                    let exitType = 'N/A';
                    if (!isEmpty(round.funding_stage_data)) {
                        exitType = round.funding_stage_data.name;
                    }

                    const acquirerNames = round.investors_data?.length > 0
                        ? round.investors_data.map(a => a?.name).filter(Boolean).join(', ')
                        : 'N/A';

                    return (
                        <div
                            key={key}
                            className="mob-investment-row"
                            onClick={() => toggleMobileRow(key)}
                        >
                            <div className="mob-investment-row__top">
                                <div className="mob-badge" style={{ background: col.bg, color: col.txt }}>
                                    {getInitials(acquiredEntity?.name)}
                                </div>
                                <div className="mob-investment-row__main">
                                    <EntityLink entity={acquiredEntity} hasDirAccess={hasDirAccess} />
                                    <div className="mob-investment-row__meta">
                                        {hq} · {primaryIndustry}
                                    </div>
                                </div>
                                <div className="mob-investment-row__right">
                                    <span className="mob-investment-row__amount">
                                        {getAmount(round.amount)}
                                    </span>
                                    <div className="mob-investment-row__status-row">
                                        <i className={`fas fa-chevron-${isOpen ? 'up' : 'down'} mob-investment-row__chevron`} />
                                    </div>
                                </div>
                            </div>

                            {isOpen && (
                                <div className="mob-investment-row__expand">
                                    <div className="mob-expand-grid">
                                        <div className="mob-expand-cell">
                                            <div className="mob-expand-cell__lbl">Exit Type</div>
                                            <div className="mob-expand-cell__val">{exitType}</div>
                                        </div>
                                        <div className="mob-expand-cell">
                                            <div className="mob-expand-cell__lbl">Exit Date</div>
                                            <div className="mob-expand-cell__val">{getDate(round.date_raised)}</div>
                                        </div>
                                        <div className="mob-expand-cell">
                                            <div className="mob-expand-cell__lbl">Years to Exit</div>
                                            <div className="mob-expand-cell__val">{round.time_to_exit || 'N/A'}</div>
                                        </div>
                                        <div className="mob-expand-cell">
                                            <div className="mob-expand-cell__lbl">Total Raised</div>
                                            <div className="mob-expand-cell__val">{getAmount(round.total_amount_raised)}</div>
                                        </div>
                                        <div className="mob-expand-cell">
                                            <div className="mob-expand-cell__lbl">Deal Count</div>
                                            <div className="mob-expand-cell__val">{round.investedin_funding_count || 'N/A'}</div>
                                        </div>
                                        <div className="mob-expand-cell">
                                            <div className="mob-expand-cell__lbl">Acquirer</div>
                                            <div className="mob-expand-cell__val">{acquirerNames}</div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default PortfolioExitsTable;
